/**
 * TimeTrack - App Entry
 *
 * WICHTIG: Teamerfasser koennen NUR den heutigen Tag bearbeiten!
 *
 * Routing:
 * - /           -> Redirect zu /today
 * - /today      -> TimesheetPage (heute) - Timer-basierte Erfassung
 * - /admin      -> AdminOverviewView (Office/Admin Client)
 * - /admin/employees -> EmployeesView
 * - /admin/timesheets/:id -> TimeSheetDetailView (Detail mit Entries)
 * - /admin/timesheets/:id/entries/:entryId -> EntryDetailView (mit Karte + Edit)
 * - /dev        -> DevView (Development Tools)
 */

import { signal } from '@preact/signals';
import type { ComponentChild } from 'preact';
import { lazy, Suspense } from 'preact/compat';
import { Loader2 } from 'lucide-preact';

import { TimesheetPage } from './views/timesheet';
import { DevView } from './views/DevView';
import { DemoWidget } from './components/demo/DemoWidget';

// Lazy-load Admin-Bereich (nur fuer /admin Nutzer)
const AdminRoutes = lazy(() =>
  import('./views/admin/AdminRoutes').then((m) => ({ default: m.AdminRoutes }))
);

// =============================================================================
// Simple Router
// =============================================================================

/** Aktueller Pfad - exportiert fuer Layout-Komponenten */
export const currentPath = signal(window.location.pathname);

/** Entry-ID fuer Panel-Ansicht (aus ?entry= Query-Parameter) */
export const entryPanelId = signal<string | null>(
  new URLSearchParams(window.location.search).get('entry')
);

/**
 * Aktualisiert entryPanelId aus der aktuellen URL.
 */
function syncEntryPanelFromUrl(): void {
  entryPanelId.value = new URLSearchParams(window.location.search).get('entry');
}

// Auf popstate reagieren (Browser-Navigation)
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    currentPath.value = window.location.pathname;
    syncEntryPanelFromUrl();
  });
}

/**
 * Einfache Navigation (ohne Page Reload).
 */
export function navigate(path: string): void {
  if (path !== currentPath.value) {
    window.history.pushState(null, '', path);
    currentPath.value = path;
    syncEntryPanelFromUrl();
  }
}

/**
 * Setzt den Entry-Panel Query-Parameter.
 * Aendert nur den Query-String, nicht den Pfad.
 */
export function setEntryPanel(entryId: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set('entry', entryId);
  window.history.pushState(null, '', url.toString());
  entryPanelId.value = entryId;
}

/**
 * Entfernt den Entry-Panel Query-Parameter.
 */
export function closeEntryPanel(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('entry');
  window.history.pushState(null, '', url.toString());
  entryPanelId.value = null;
  closeRightPanel();
}

// =============================================================================
// Generisches Right-Panel
// =============================================================================

/** Generischer Panel-Content - beliebiges JSX */
export const rightPanelContent = signal<ComponentChild | null>(null);

/** Panel-Titel */
export const rightPanelTitle = signal<string>('');

/**
 * Oeffnet das Right-Panel mit beliebigem Content.
 */
export function openRightPanel(title: string, content: ComponentChild): void {
  rightPanelTitle.value = title;
  rightPanelContent.value = content;
}

/**
 * Schliesst das Right-Panel.
 */
export function closeRightPanel(): void {
  rightPanelContent.value = null;
  rightPanelTitle.value = '';
}

/**
 * Bestimmt die Default-Route basierend auf User-Rolle.
 * TODO: Spaeter aus Auth-Context/Store holen.
 */
function getDefaultRoute(): string {
  // TODO: Role-basiertes Routing
  // const role = getCurrentUserRole();
  // if (role === 'SUPERVISOR') return '/admin';
  // if (role === 'PAYROLL') return '/admin';
  return '/today';
}

/**
 * Prueft ob ein Pfad eine bekannte Route ist.
 * WICHTIG: /days und /day/:date wurden entfernt (Force Today).
 */
function isKnownRoute(path: string): boolean {
  // Statische Routen
  const staticRoutes = ['/', '/today', '/admin', '/admin/employees', '/dev'];
  if (staticRoutes.includes(path)) return true;

  // Dynamische Routen (Pattern-Matching)
  // /admin/timesheets/:id
  if (/^\/admin\/timesheets\/[^/]+$/.test(path)) return true;
  // /admin/timesheets/:id/entries/:entryId
  if (/^\/admin\/timesheets\/[^/]+\/entries\/[^/]+$/.test(path)) return true;

  return false;
}

// =============================================================================
// App Component
// =============================================================================

/**
 * Rendert die aktuelle Route.
 * Separiert von App, damit DemoWidget immer gezeigt werden kann.
 */
function RouteContent() {
  const path = currentPath.value;

  // Dev Tools
  if (path === '/dev') {
    return <DevView />;
  }

  // Admin Routes - lazy loaded
  if (path.startsWith('/admin')) {
    return (
      <Suspense
        fallback={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        }
      >
        <AdminRoutes />
      </Suspense>
    );
  }

  // Root oder unbekannte Route: Redirect zur Default-Route
  if (path === '/' || path === '' || !isKnownRoute(path)) {
    const defaultRoute = getDefaultRoute();
    if (path !== defaultRoute) {
      navigate(defaultRoute);
      return null;
    }
  }

  // TimesheetPage fuer heute (Timer-basierte Erfassung)
  // WICHTIG: Nur /today - keine Datumsauswahl fuer Teamerfasser!
  return <TimesheetPage />;
}

export function App() {
  return (
    <>
      <RouteContent />
      <DemoWidget />
    </>
  );
}
